import { IntakeFormState } from './intake-form-state';
import { DeclaredState, FitnessState, ImmutableState, usesCalorieCycling } from './user-state';
import { getSelectedTrainingDedicationLevel } from './intake-form-state';
import { AdvancedMethodsRMTesting, DedicationLevel, TrainingGoal } from '../enums';
import { isRuleActiveOnDate, Schedule, Schedule_ItemType, ScheduleRuleRepeat } from './schedule';
import { roundToPrecision } from '../shared-util';
import { constraintIntakeDeltaPercentForDedicationLevel } from '../intake-delta';
import { REST_TIMER_DEFAULT_SECONDS, RestTimerStart } from './rest-timer';

/** The saved records an intake form in change mode is pre-filled from. */
export interface IntakeFormPrefillSources {
  /** The user's saved answers from their last intake. */
  declaredState: DeclaredState;
  /** The user's current measurements — bodyweight and per-exercise strength. */
  fitnessState: FitnessState;
  /** The user's schedule, which the seven training-day flags are derived from. */
  schedule: Schedule;
  /** Today's LOCAL date, as `yyyy-mm-dd`. Decides which schedule rules count as active. */
  todayISO: string;
  /** The unit the user reads their bodyweight in, from their client-side settings. */
  bodyweightUnit: string | undefined;
}

/**
 * Copy the user's saved answers into the intake form, for change mode.
 *
 * This is the inbound partner to mapIntakeFormToDeclaredState and
 * mapIntakeFormToImmutableState above, which map the same answers back out.
 *
 * Unlike those two, this one WRITES onto the intake form rather than returning
 * a Partial for the caller to merge in. intakeFormState is a live PouchDB
 * document: CLAUDE.md forbids Object.assign() on one, and deepOverwriteCouchSafe()
 * deep-merges, which would behave differently from the plain overwrite the intake
 * form has always done for the object-valued fields here (exerciseStats,
 * macroTargets). Assigning directly is what keeps this a behaviour-preserving
 * extraction of the two blocks it replaces.
 *
 * todayISO and bodyweightUnit are passed in because they come from client
 * services (the debug clock and the settings service) that shared code cannot reach.
 *
 * The caller keeps whatever step is its own — repairing the declared state's
 * training days, or falling back to the fitness state for body-fat data.
 */
export function applySavedStateToIntakeForm(intake: IntakeFormState, sources: IntakeFormPrefillSources): void {
  const { declaredState, fitnessState, schedule, todayISO, bodyweightUnit } = sources;

  intake.weight_kg = fitnessState.weight_kg;
  intake.weight_unit = bodyweightUnit;
  intake.declaredStrength = declaredState.declaredStrength;
  intake.exerciseStats = fitnessState.exerciseStats;
  intake.activityLevel = declaredState.activityLevel;
  intake.bedTime = declaredState.bedTime;
  intake.wakeupTime = declaredState.wakeupTime;
  intake.sleepHours = declaredState.sleepHours;
  intake.sleepQuality = declaredState.sleepQuality;
  intake.stressLevel = declaredState.stressLevel;
  // for each of the 7 weekdays, set the flag if a weekly workout rule is active on it today
  intake.trainingDays = Array.from({ length: 7 }, (_, i) => schedule.rules.filter(rule => rule.itemType === Schedule_ItemType.Workout && rule.repeat === ScheduleRuleRepeat.Weekly && isRuleActiveOnDate(rule, todayISO)).map(rule => rule.weekday).includes(i + 1));
  intake.trainingFrequency = intake.trainingDays.filter(d => d).length; // count the number of days that are true
  intake.lastTrainingFrequency = intake.trainingFrequency;
  intake.trainingVolumeWeeklyAvg = declaredState.trainingVolumeWeeklyAvg;
  intake.intakeDeltaPercent = declaredState.intakeDeltaPercent;
  // the legacy stored shape was a boolean; convert it to the TrainingGoal enum
  intake.trainingGoalGrowth = typeof declaredState.trainingGoalGrowth === 'boolean'
    ? declaredState.trainingGoalGrowth
      ? TrainingGoal.MuscleGrowth
      : TrainingGoal.FatLoss
    : declaredState.trainingGoalGrowth;
  // #4591: the RAW stored value, deliberately not the resolved one. Seeding
  // 'optimal' where the user has never chosen would differ from a baseline
  // snapshot written before this feature existed, and every existing user
  // would be told their program needs rebuilding the first time they open
  // this form. The template shows Optimal for an unset value anyway.
  intake.trainingSplitMode = declaredState.trainingSplitMode;
  intake.muscleGrowthPreferenceMain = declaredState.muscleGrowthPreferenceMain;
  intake.muscleGrowthPreferenceTable = declaredState.muscleGrowthPreferenceTable;
  intake.advancedMethodsMyoReps = declaredState.advancedMethodsMyoReps;
  intake.advancedMethodsRMTesting = declaredState.advancedMethodsRMTesting || AdvancedMethodsRMTesting.Prefer;
  intake.advancedMethodsCalorieCycling = usesCalorieCycling(declaredState);
  intake.morningAppetite = declaredState.morningAppetite;
  intake.mealFrequency = declaredState.mealFrequency;
  intake.nutritionCoaching = declaredState.nutritionCoaching;
  intake.nutritionTracking = declaredState.nutritionTracking;
  intake.macroTargets = declaredState.macroTargets;
}

/**
 * Map IntakeFormState fields to DeclaredState.
 * Pure function — no mode checks, no previous-state comparisons.
 * Used by both intake-form.ts (client) and coach-claim (after claiming).
 *
 * `seedNewUserDefaults` is set by callers that are constructing a fresh
 * plan (new intake completion, coach-claim finalization). Those callers
 * get the rest-timer defaults merged in; edit-mode callers must leave it
 * off so a re-save of an existing user's intake does not migrate their
 * `restTimerEnabled` from undefined/false to true.
 */
export function mapIntakeFormToDeclaredState(
  intake: IntakeFormState,
  opts: { seedNewUserDefaults?: boolean } = {},
): Partial<DeclaredState> {
  return {
    activityLevel: intake.activityLevel,
    bedTime: intake.bedTime,
    declaredStrength: intake.declaredStrength,
    morningAppetite: intake.morningAppetite,
    mealFrequency: intake.mealFrequency,
    muscleGrowthPreferenceMain: intake.muscleGrowthPreferenceMain,
    muscleGrowthPreferenceTable: intake.muscleGrowthPreferenceTable,
    sleepQuality: intake.sleepQuality,
    sleepHours: intake.sleepHours,
    stressLevel: intake.stressLevel,
    trainingDays: intake.trainingDays,
    trainingGoalGrowth: intake.trainingGoalGrowth,
    wakeupTime: intake.wakeupTime,
    advancedMethodsMyoReps: intake.advancedMethodsMyoReps,
    advancedMethodsRMTesting: intake.advancedMethodsRMTesting,
    advancedMethodsCalorieCycling: intake.advancedMethodsCalorieCycling,
    nutritionCoaching: intake.nutritionCoaching,
    nutritionTracking: intake.nutritionTracking,
    macroTargets: intake.macroTargets,
    intakeDeltaPercent: intake.intakeDeltaPercent,
    trainingVolumeWeeklyAvg: intake.trainingVolumeWeeklyAvg,
    trainingSplitMode: intake.trainingSplitMode,
    trainingDedicationLevel: getSelectedTrainingDedicationLevel(intake),
    nutritionDedicationLevel: getSelectedNutritionDedicationLevel(intake),
    // seed auto-start ON for new users so they get the category-standard
    // experience; existing users load with both fields `undefined` (read as
    // Manual). Gated on seedNewUserDefaults so re-running the mapper on an
    // edit of an existing user's intake does not silently migrate them.
    // both fields are written, as applyRestTimerStart does.
    ...(opts.seedNewUserDefaults ? {
      restTimerStart: RestTimerStart.Auto,
      restTimerEnabled: true,
      defaultRestSeconds: REST_TIMER_DEFAULT_SECONDS,
    } : {}),
  };
}

/**
 * Map IntakeFormState fields to ImmutableState.
 * Pure function — no mode checks.
 */
export function mapIntakeFormToImmutableState(intake: IntakeFormState): Partial<ImmutableState> {
  return {
    ankle_cm: intake.ankle_cm,
    ankle_unit: intake.ankle_unit,
    wrist_cm: intake.wrist_cm,
    wrist_unit: intake.wrist_unit,
    born: intake.born,
    gender: intake.gender,
    height_cm: intake.height_cm,
    height_unit: intake.height_unit,
    longerFinger: intake.longerFinger,
  };
}

/**
 * Determine the nutrition dedication level from the intake form state.
 * Extracted from intake-form.ts for reuse in claim flow.
 */
export function getSelectedNutritionDedicationLevel(intake: IntakeFormState): DedicationLevel | undefined {
  if (!intake.maxEffortPlan) {
    return undefined;
  }
  const slowAndSteadyDelta = Math.abs(getIntakeDeltaPercentForDedicationLevel(intake, DedicationLevel.SlowAndSteady));
  const maxEffortDelta = Math.abs(getIntakeDeltaPercentForDedicationLevel(intake, DedicationLevel.MaximumEffort));
  const currentDelta = Math.abs(intake.intakeDeltaPercent);

  if (currentDelta <= slowAndSteadyDelta) {
    return DedicationLevel.SlowAndSteady;
  } else if (currentDelta > slowAndSteadyDelta && currentDelta < maxEffortDelta) {
    return DedicationLevel.Balanced;
  }
  return DedicationLevel.MaximumEffort;
}

function getIntakeDeltaPercentForDedicationLevel(intake: IntakeFormState, dedicationLevel: DedicationLevel): number {
  return roundToPrecision(constraintIntakeDeltaPercentForDedicationLevel(intake.maxEffortPlan.intakeDeltaPercent, dedicationLevel) * 100, 0.1);
}
